import { api, getApiClient } from '@kolab/dms-base/api'
import type {
  DeviceActivitySummary,
  DeviceInstallation,
  DeviceLockUnlockEvent,
  DeviceLockUnlockFilters,
  DeviceReportFilters,
  ExpiringDevice,
  PageableQuery,
  PaginatedResponse,
  PoolAvailability,
  ReportPage,
  ResellerCapacity,
} from './types'
import { downloadBlob } from '../utils/csvExport'

const BASE = '/v1/reports'

export interface ListResellerCapacityQuery extends PageableQuery {
  resellerId?: string
}

export interface ListExpiringDevicesQuery extends PageableQuery {
  asOf?: string
  resellerLicenseId?: string
}

export async function listPoolAvailability(params?: PageableQuery) {
  return api.get<PaginatedResponse<PoolAvailability>>(`${BASE}/pool-availability`, { params, _suppressErrorToast: true })
    .then((r) => r.data)
}

export async function getPoolAvailability(distributorLicenseId: string) {
  return api.get<PoolAvailability>(`${BASE}/pool-availability/${distributorLicenseId}`).then((r) => r.data)
}

export async function listResellerCapacity(params?: ListResellerCapacityQuery) {
  return api.get<PaginatedResponse<ResellerCapacity>>(`${BASE}/reseller-capacity`, { params, _suppressErrorToast: true })
    .then((r) => r.data)
}

export async function getResellerCapacity(resellerLicenseId: string) {
  return api.get<ResellerCapacity>(`${BASE}/reseller-capacity/${resellerLicenseId}`).then((r) => r.data)
}

export async function listExpiringDevices(params?: ListExpiringDevicesQuery) {
  return api.get<PaginatedResponse<ExpiringDevice>>(`${BASE}/expiring-devices`, { params, _suppressErrorToast: true })
    .then((r) => r.data)
}

// -------- Device activity reports --------

export type ListDeviceInstallationsQuery = PageableQuery & DeviceReportFilters
export type ListDeviceLockUnlockQuery = PageableQuery & DeviceLockUnlockFilters

export async function getDeviceActivitySummary() {
  return api.get<DeviceActivitySummary>(`${BASE}/device-activity-summary`, { _suppressErrorToast: true })
    .then((r) => r.data)
}

export async function listDeviceInstallations(params?: ListDeviceInstallationsQuery) {
  return api.get<ReportPage<DeviceInstallation>>(`${BASE}/device-installations`, { params, _suppressErrorToast: true })
    .then((r) => r.data)
}

export async function listDeviceLockUnlock(params?: ListDeviceLockUnlockQuery) {
  return api.get<ReportPage<DeviceLockUnlockEvent>>(`${BASE}/device-lock-unlock`, { params, _suppressErrorToast: true })
    .then((r) => r.data)
}

/** Downloads the filtered installation report as CSV (server-generated). */
export async function exportDeviceInstallations(filters: DeviceReportFilters) {
  return downloadReportCsv(`${BASE}/device-installations/export`, filters, 'device-installation-report.csv')
}

/** Downloads the filtered lock & unlock report as CSV (server-generated). */
export async function exportDeviceLockUnlock(filters: DeviceLockUnlockFilters) {
  return downloadReportCsv(`${BASE}/device-lock-unlock/export`, filters, 'device-lock-unlock-report.csv')
}

/**
 * Fetches a CSV export and saves it under the server's Content-Disposition
 * filename. A 4xx (row cap exceeded, invalid filter, no permission) arrives as
 * a JSON envelope inside the Blob, which the shared interceptor cannot read —
 * so 4xx responses other than 401 (which must still reach the interceptor for
 * the token refresh) are resolved here and rethrown with the server's message.
 * Rejects with `{ message, status }` like every other API call.
 */
async function downloadReportCsv(url: string, params: object, fallbackName: string) {
  const res = await getApiClient().get<Blob>(url, {
    params,
    responseType: 'blob',
    _suppressErrorToast: true,
    validateStatus: (s) => (s >= 200 && s < 300) || (s >= 400 && s < 500 && s !== 401),
  })
  if (res.status >= 400) {
    throw { message: await blobErrorMessage(res.data, res.status), status: res.status }
  }
  downloadBlob(filenameFrom(res.headers['content-disposition']) ?? fallbackName, res.data)
}

async function blobErrorMessage(body: Blob | undefined, status: number): Promise<string> {
  if (status === 403) return "You don't have permission to export this report."
  try {
    const json = JSON.parse(await (body as Blob).text()) as { message?: string; data?: { message?: string } }
    return json.data?.message || json.message || 'Export failed.'
  } catch {
    return 'Export failed.'
  }
}

export function filenameFrom(contentDisposition: unknown): string | undefined {
  if (typeof contentDisposition !== 'string') return undefined
  const m = /filename="?([^";]+)"?/i.exec(contentDisposition)
  return m?.[1]
}
